import React, { useContext, useState } from "react";
import {
  FilterData,
  FilterPayload,
  LogPermission,
} from "../../Networking/Interface/Logs/LogInterface";
import { LogType } from "../../Utils/enums";
import { DateInput } from "./DateInput";
import { FilterItem } from "./FilterItem";
import "./Logs.css";
import { APIResponse, Log } from "./LogView";
import Button from "../../ui/Button";
import { SunsetAPILogContext } from "../../Networking/Interface/Logs/LogAPIProvider";
import { useToast } from "../../Components/Toast/ToastContext";
import { ToastType } from "../../Components/Toast/Toast";

interface LogFilterProps {
  onSubmit: (response: APIResponse<Log[]>) => void;
  onLoadingStart: () => void;
  allowedOptions: LogPermission[];
}

const getDefaultFromDate = () => {
  const d = new Date();
  d.setHours(d.getHours() - 1);
  return d;
};

const LogFilter: React.FC<LogFilterProps> = ({ onSubmit, onLoadingStart, allowedOptions }) => {
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(new Date());
  const [filters, setFilters] = useState<FilterData[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const api = useContext(SunsetAPILogContext);
  const { showToast } = useToast();

  const calculateTimezoneOffset = () => {
    const now = new Date();
    const offset = -now.getTimezoneOffset() / 60;
    return offset >= 0 ? `+${offset}` : `${offset}`;
  };

  const handleAddFilter = () => {
    setFilters((prev) => [
      ...prev,
      {
        type: LogType.Chat,
        subcategories: [],
        log: "",
        caseSensitive: false,
        fromID: null,
      },
    ]);
  };

  const handleChangeFilter = (
    index: number,
    key: keyof FilterData,
    value: LogType | string | string[]
  ) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [key]: value };
    setFilters(newFilters);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    if (fromDate >= toDate) {
      showToast(ToastType.Error, "'From' date must be before 'To' date.", 5);
      return;
    }
    if (filters.length === 0) {
      showToast(ToastType.Error, "Add at least one filter before searching.", 5);
      return;
    }

    onLoadingStart();
    const requestData: FilterPayload = {
      fromDateUTC: fromDate.toISOString(),
      toDateUTC: toDate.toISOString(),
      filters: filters.map((filter) => ({
        type: filter.type,
        subcategories: filter.subcategories,
        log: filter.log,
        caseSensitive: false,
        fromID: filter.fromID,
      })),
    };

    try {
      const data = await api?.postLogFilter(requestData);
      onSubmit(data!);
      setIsCollapsed(true);
    } catch (error) {
      console.error("Error sending request:", error);
      onSubmit({ success: false, code: 500, error: "Request failed. Please try again." });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="log-filter-panel" onKeyDown={handleKeyDown}>
      <div
        className="log-filter-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className="log-filter-title">Filters</span>
        <span className="log-filter-toggle">{isCollapsed ? "▼ Show" : "▲ Hide"}</span>
      </div>

      {!isCollapsed && (
        <div className="log-filter-body">
          <div className="log-date-row">
            <div className="log-date-field">
              <DateInput label="From" value={fromDate} onChange={setFromDate} />
            </div>
            <div className="log-date-field">
              <DateInput label="To" value={toDate} onChange={setToDate} />
            </div>
          </div>

          <p className="timezone-info">
            Server logs use UTC time. Your offset is UTC{calculateTimezoneOffset()}h.
          </p>

          {filters.map((filter, idx) => (
            <div key={idx} style={{ position: 'relative', zIndex: filters.length - idx }}>
              <FilterItem
                idx={idx}
                filter={filter}
                onChange={handleChangeFilter}
                onRemove={handleRemoveFilter}
                allowedOptions={allowedOptions}
              />
            </div>
          ))}

          <div className="log-filter-actions">
            <Button onClick={handleAddFilter}>+ Add Filter</Button>
            <Button onClick={handleSubmit}>Search</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogFilter;
